import {
  JobSummaryFragment,
  JobType,
  QuoteEventType,
} from '@shared/generated/graphql';
import { RelatedExternalMessage } from '@shared/plugin/pages/Home/components/email-thread-activity/types';
import { ChatMessageEvent, Conversation } from '@shared/types/conversation';
import { ExternalMessageClassification } from 'clerk_common';

export type FeedFilterParams = {
  selectedOption: FeedFilterOption;
  setSelectedOption: (state: FeedFilterOption) => void;
  filterOptions: FeedFilterOption[];
};

export enum FeedFilterOption {
  ACTIVITY = 'ACTIVITY',
  QUOTES = 'QUOTES',
  ORDERS = 'ORDERS',
}

export enum FeedItemType {
  ACTION = 'ACTION',
  CHAT_MESSAGE = 'CHAT_MESSAGE',
  COMMENT = 'COMMENT',
  CONVERSATION = 'CONVERSATION',
  EMPTY_STATE = 'EMPTY_STATE',
  JOB_CARD = 'JOB_CARD',
  MESSAGE_CLASSIFICATION = 'MESSAGE_CLASSIFICATION',
  QUOTE_EVENTS = 'QUOTE_EVENTS',
  SUGGESTED_ACTIONS = 'SUGGESTED_ACTIONS',
  TRACKED_MOVEMENT = 'TRACKED_MOVEMENT',
}

export type FeedItem =
  | SuggestedActionsFeedItem
  | ActionFeedItem
  | QuoteEventsFeedItem
  | CommentFeedItem
  | MessageClassificationFeedItem
  | JobCardFeedItem
  | ConversationFeedItem
  | EmptyStateFeedItem
  | ChatMessageFeedItem
  | TrackedMovementFeedItem;

export type FeedItemUser = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

type FeedItemBase = {
  createdAt?: string;
  createdByDisplay?: string;
  createdBy?: FeedItemUser;
  priority?: number;
  type: FeedItemType;
};

export type ChatMessageFeedItem = FeedItemBase & {
  type: FeedItemType.CHAT_MESSAGE;
  message: ChatMessageEvent;
};

export type EmptyStateFeedItem = FeedItemBase & {
  type: FeedItemType.EMPTY_STATE;
};

export type ActionFeedItem = FeedItemBase & {
  type: FeedItemType.ACTION;
};

export type FeedQuoteEvent = {
  createdAt: string;
  type: QuoteEventType;
  quoteId: string;
  metadata?: any;
};

export type QuoteEventsFeedItem = FeedItemBase & {
  type: FeedItemType.QUOTE_EVENTS;
  data: {
    quoteEvents: FeedQuoteEvent[];
  };
};

export type ConversationFeedItem = FeedItemBase & {
  type: FeedItemType.CONVERSATION;
  data: {
    conversation: Conversation;
  };
};

export type CommentFeedItem = FeedItemBase & {
  type: FeedItemType.COMMENT;
};

export type JobCardFeedItem = FeedItemBase & {
  type: FeedItemType.JOB_CARD;
  data: {
    job: JobSummaryFragment;
    initialJobType: JobType;
  };
};

export type MessageClassificationFeedItem = FeedItemBase & {
  type: FeedItemType.MESSAGE_CLASSIFICATION;
  data: RelatedExternalMessage & {
    maxConfidenceClass: ExternalMessageClassification | null;
  };
};

export type SuggestedActionsFeedItem = FeedItemBase & {
  type: FeedItemType.SUGGESTED_ACTIONS;
  data: SuggestedActionsData;
};

export type TrackedMovementFeedItem = FeedItemBase & {
  type: FeedItemType.TRACKED_MOVEMENT;
  data: {
    movementId: string;
    shipmentId: string;
    referenceNumber?: string | null;
    isPaused: boolean;
    onPausedChange: () => void;
  };
};

export type SuggestedActionsData = {
  actions: SuggestedAction[];
};

export enum SuggestedActionType {
  BUILD_ORDER = 'BUILD_ORDER',
  BUILD_QUOTE = 'BUILD_QUOTE',
  BUILD_QUOTE_WITH_HINT = 'BUILD_QUOTE_WITH_HINT',
  BUILD_REMAINING_ORDERS = 'BUILD_REMAINING_ORDERS',
  DRAFT_QUOTE_REPLY = 'DRAFT_QUOTE_REPLY',
  BATCH_QUOTE_REPLY = 'BATCH_QUOTE_REPLY',
  CANCEL_BATCH_QUOTE_REPLY = 'CANCEL_BATCH_QUOTE_REPLY',
  OPEN_LINK = 'OPEN_LINK',
  MARK_QUOTE_QUOTED = 'MARK_QUOTE_QUOTED',
  MARK_QUOTE_WON = 'MARK_QUOTE_WON',
}

export type SuggestedAction =
  | BuildOrderAction
  | BuildQuoteAction
  | BuildQuoteWithHintAction
  | BuildRemainingOrdersAction
  | DraftQuoteReplyAction
  | BatchQuoteReplyAction
  | CancelBatchQuoteReplyAction
  | OpenLinkAction
  | UpdateQuoteStatusAction;

export type BuildOrderAction = {
  type: SuggestedActionType.BUILD_ORDER;
};

export type OpenLinkAction = {
  type: SuggestedActionType.OPEN_LINK;
  url: string;
  title: string;
};

export type BuildQuoteAction = {
  type: SuggestedActionType.BUILD_QUOTE;
};

export type BuildQuoteWithHintAction = {
  type: SuggestedActionType.BUILD_QUOTE_WITH_HINT;
};

export type BuildRemainingOrdersAction = {
  type: SuggestedActionType.BUILD_REMAINING_ORDERS;
  orderId: string;
};

export type DraftQuoteReplyAction = {
  type: SuggestedActionType.DRAFT_QUOTE_REPLY;
  quoteId: string;
};

export type BatchQuoteReplyAction = {
  type: SuggestedActionType.BATCH_QUOTE_REPLY;
  jobId: string;
};

export type CancelBatchQuoteReplyAction = {
  type: SuggestedActionType.CANCEL_BATCH_QUOTE_REPLY;
};

export type UpdateQuoteStatusAction = {
  type:
    | SuggestedActionType.MARK_QUOTE_QUOTED
    | SuggestedActionType.MARK_QUOTE_WON;
  quoteId: string;
};
